import { useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { monthShort } from '../../utils/intlFormatters';

// A year of dots reads at a glance; past that the row becomes a bar chart with
// no axis, so the rest stay behind "show all".
const FIRST_PAGE = 12;

function monthsFrom(startDate, count, today) {
  const [y, m] = String(startDate).slice(0, 10).split('-').map(Number);
  const nowKey = `${today.getFullYear()}-${today.getMonth()}`;
  return Array.from({ length: count }, (_, i) => {
    const month = (m - 1 + i) % 12;
    const year = y + Math.floor((m - 1 + i) / 12);
    const key = `${year}-${month}`;
    return { key, label: monthShort(month), isCurrent: key === nowKey };
  });
}

/**
 * A goal saved by a monthly amount, month by month: one circle per month the
 * plan needs, filled for the ones its saving covers so far.
 */
export default function GoalMonthDots({ goal, summary }) {
  const { t } = useTranslation('goals');
  const [showAll, setShowAll] = useState(false);
  const monthly = goal.monthly_contribution;
  if (!monthly || monthly <= 0) return null;

  const total = Math.ceil(goal.target_amount / monthly);
  const done = Math.min(total, Math.max(0, Math.floor(summary.saved / monthly)));
  const months = monthsFrom(goal.start_date, total, new Date());
  const visible = showAll ? months : months.slice(0, FIRST_PAGE);

  return (
    <Box>
      <Text fontSize="xs" color="redesign.text.muted" mb={2}>
        {t('monthDots.heading', { done, total })}
      </Text>
      <Flex wrap="wrap" gap={2}>
        {visible.map((month, index) => (
          <Flex key={month.key} direction="column" align="center" gap={1} w="26px">
            <Flex
              w="12px"
              h="12px"
              borderRadius="full"
              border="1.5px solid"
              align="center"
              justify="center"
              borderColor={index < done ? 'redesign.accent.green' : month.isCurrent ? 'redesign.text.muted' : 'redesign.border'}
              bg={index < done ? 'redesign.accent.green' : 'transparent'}
            >
              {/* The month now due: a centre dot, so it reads apart from the
                  months already covered and the ones still ahead. */}
              {index >= done && month.isCurrent && (
                <Box w="4px" h="4px" borderRadius="full" bg="redesign.text.muted" />
              )}
            </Flex>
            <Text
              fontSize="2xs"
              fontWeight={month.isCurrent ? 600 : 400}
              color={month.isCurrent ? 'redesign.heading' : 'redesign.text.muted'}
            >
              {month.label}
            </Text>
          </Flex>
        ))}
      </Flex>
      {months.length > FIRST_PAGE && (
        <Text
          as="button"
          type="button"
          mt={2}
          fontSize="xs"
          color="redesign.accent.blue"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? t('monthDots.showLess')
            : t('monthDots.showAll', { shown: FIRST_PAGE, total: months.length })}
        </Text>
      )}
    </Box>
  );
}
